import { useState, useEffect } from 'react';
import { useBranch } from '../contexts/BranchContext';
import { DEFAULT_HEIGHTS, heightLabels } from '../utils/pricing.js';
import Toast from '../components/Toast';

export default function BuffetPricing({ apiUrl }) {
  const { currentBranch, effectiveBranchId, isAllBranches } = useBranch();
  // ราคาบุฟเฟ่ต์ตั้งแยกต่อสาขาได้แล้ว — ต้องเลือกสาขาที่ชัดเจนก่อนถึงจะโหลด/แก้ไขได้
  // เหมือนหน้าหมวดหมู่/เมนู ไม่งั้นจะไม่รู้ว่ากำลังแก้ราคาของสาขาไหน
  const blocked = isAllBranches || !effectiveBranchId;
  // childFree ไม่มีช่องให้แก้แล้ว (เด็กต่ำกว่าเกณฑ์ = ฟรีเสมอ) แต่ต้องเก็บไว้ใน state
  // เพื่อส่งกลับตอนบันทึก ไม่งั้นค่าที่เคยตั้งไว้จะหายจาก DB
  const [prices, setPrices] = useState({
    adult: 0, childBig: 0, childSmall: 0, childFree: 0, drinkRefill: 0,
    drinkRefillLabel: 'รีฟิลน้ำไม่อั้น',
    heights: { ...DEFAULT_HEIGHTS }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (blocked) {
      setLoading(false);
      return;
    }
    loadPricing();
  }, [effectiveBranchId, blocked]);

  // ช่วงโหลดหน้าแรก branch header ที่แนบอัตโนมัติ (installBranchFetch) บางทียังไม่ทันพร้อม
  // ก่อน component นี้ mount ทำให้ request แรกได้ 400 เป็นบางครั้ง — เดิมโค้ดไม่เช็ค
  // response.ok เลย พอพลาดเลยปล่อยราคาเริ่มต้น (0 ทั้งหมด) ค้างเงียบๆ โดยไม่มี error
  // ให้เห็น ดูเหมือนราคาที่เคยตั้งไว้หายไป ทั้งที่จริงยังอยู่ครบใน DB — retry ให้เองครั้งเดียว
  // ก่อน แล้วค่อยแจ้ง error ชัดเจนถ้ายังไม่สำเร็จ
  const loadPricing = async (isRetry = false) => {
    let ok = false;
    try {
      // endpoint นี้ตั้งใจให้ public (แอปลูกค้าที่ไม่ล็อกอินเรียกได้ผ่าน ?branch=slug) แต่ฝั่ง
      // แอดมินต้องแนบ token ด้วย ไม่งั้น backend ไม่รู้ว่าเป็นเจ้าของร้าน (isOwner) เลยไม่ยอม
      // เชื่อ X-Branch-Id ที่ installBranchFetch แนบมาอัตโนมัติ (กันคนแปลกหน้าสวมรอยเลือก
      // สาขาผ่าน header) แล้วตกไปใช้ fallback "สาขาเดียวที่ active" ซึ่งพังทันทีที่มี 2
      // สาขา active พร้อมกัน — นี่คือสาเหตุจริงของ 400 ที่เจอ ไม่ใช่ปัญหา timing
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${apiUrl}/api/settings/pricing`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.data) setPrices(prev => ({ ...prev, ...data.data }));
        ok = true;
      }
    } catch (err) {
      console.error('Failed to load pricing:', err);
    }

    if (ok) {
      setLoading(false);
      return;
    }
    if (!isRetry) {
      // request แรกพลาด — รอสักครู่แล้วลองอีกครั้งก่อนฟันธงว่า error จริง (ไม่ set loading
      // false ตรงนี้ เพื่อไม่ให้หน้าโชว่าราคา 0 ค้างระหว่างรอ retry)
      setTimeout(() => loadPricing(true), 800);
      return;
    }
    setMessage({ type: 'error', text: 'โหลดราคาปัจจุบันไม่สำเร็จ — ตัวเลขที่เห็นอาจไม่ตรงกับที่ตั้งไว้จริง กรุณารีเฟรชหน้า' });
    setLoading(false);
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${apiUrl}/api/settings/pricing`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(prices)
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'บันทึกราคาสำเร็จ' });
      } else {
        const err = await response.json();
        setMessage({ type: 'error', text: err.message || 'เกิดข้อผิดพลาด' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้' });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (key, value) => {
    const parsed = parseFloat(value);
    setPrices(prev => ({ ...prev, [key]: isNaN(parsed) ? 0 : parsed }));
  };

  const handleLabelChange = (key, value) => {
    setPrices(prev => ({ ...prev, [key]: value }));
  };

  const handleHeightChange = (key, value) => {
    const parsed = parseInt(value, 10);
    setPrices(prev => ({
      ...prev,
      heights: { ...DEFAULT_HEIGHTS, ...prev.heights, [key]: isNaN(parsed) ? '' : parsed }
    }));
  };

  const H = { ...DEFAULT_HEIGHTS, ...(prices.heights || {}) };
  const labels = heightLabels(prices);
  // ส่วนสูงต้องเรียงจากมากไปน้อย ไม่งั้นช่วงราคาจะซ้อนกันหรือเกิดช่องว่างที่ไม่มีราคารองรับ
  const heightError = !(Number(H.adultMin) > Number(H.childBigMin) && Number(H.childBigMin) > Number(H.childSmallMin))
    ? 'ส่วนสูงต้องเรียงจากมากไปน้อย: ผู้ใหญ่ > เด็กโต > เด็กเล็ก'
    : null;

  const exampleGuests = { adults: 2, childrenBig: 1, childrenSmall: 1 };
  const exampleTotal =
    exampleGuests.adults * prices.adult +
    exampleGuests.childrenBig * prices.childBig +
    exampleGuests.childrenSmall * prices.childSmall;

  if (blocked) {
    return (
      <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ padding: '1.25rem', background: '#1d1a12', border: '1px solid #4a3c1c', color: 'var(--warn)', borderRadius: '10px', fontWeight: 600 }}>
          ⚠️ กรุณาเลือกสาขาที่แน่นอนจากเมนูด้านบนก่อน — ราคาบุฟเฟ่ต์ตั้งแยกต่อสาขาแล้ว
          จะเลือก "รวมทุกสาขา" ไม่ได้เพื่อป้องกันการตั้งราคาผิดสาขา
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
        <div>กำลังโหลดข้อมูล...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem', maxWidth: '800px', margin: '0 auto' }}>
      <div className="page-header">
        <h2>💰 ตั้งราคาบุฟเฟ่ต์ — {currentBranch?.name}</h2>
      </div>

      <Toast message={message} onDismiss={() => setMessage(null)} />

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'grid', gap: '1.5rem' }}>
          {[
            { key: 'adult', label: '🧑 ผู้ใหญ่', desc: `ราคาต่อหัว — ${labels.adult}` },
            { key: 'childBig', label: '👦 เด็กโต', desc: `ราคาต่อหัว — ${labels.childBig}` },
            { key: 'childSmall', label: '🧒 เด็กเล็ก', desc: `ราคาต่อหัว — ${labels.childSmall}` },
          ].map(({ key, label, desc }) => (
            <div key={key} style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '1rem',
              padding: '1rem',
              background: 'var(--surface-2)',
              borderRadius: '8px',
              color: 'var(--text)'
            }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '1rem' }}>{label}</div>
                <div style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>{desc}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="number"
                  className="dark-input"
                  min="0"
                  step="1"
                  value={prices[key]}
                  onChange={(e) => handleChange(key, e.target.value)}
                  style={{
                    width: '120px',
                    padding: '0.6rem 1rem',
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    textAlign: 'right',
                    border: '1px solid var(--border-strong)',
                    borderRadius: '8px'
                  }}
                />
                <span style={{ fontWeight: 600, color: 'var(--muted)' }}>฿</span>
              </div>
            </div>
          ))}

          {/* รีฟิลน้ำ (add-on) — คนละแบบจาก 4 แถวบน เพราะแก้ได้ทั้งชื่อและราคา ไม่ใช่แค่ราคา */}
          <div style={{
            padding: '1rem',
            background: 'var(--surface-2)',
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
            color: 'var(--text)'
          }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '1rem' }}>🥤 รีฟิลเสริม (เสนอตอนเปิดโต๊ะ)</div>
              <div style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>
                บาท/คน — ใส่ 0 เพื่อปิดการเสนอขาย (ปิดโดยดีฟอลต์)
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '160px' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>
                  ชื่อที่แสดงให้ลูกค้าเห็น (ภาษาไทย)
                </label>
                <input
                  type="text"
                  className="dark-input"
                  maxLength={64}
                  value={prices.drinkRefillLabel ?? ''}
                  onChange={(e) => handleLabelChange('drinkRefillLabel', e.target.value)}
                  placeholder="รีฟิลน้ำไม่อั้น"
                  style={{
                    width: '100%',
                    padding: '0.6rem 0.9rem',
                    fontSize: '1rem',
                    border: '1px solid var(--border-strong)',
                    borderRadius: '8px',
                    boxSizing: 'border-box'
                  }}
                />
                <div style={{ fontSize: '0.75rem', color: 'var(--faint)', marginTop: '0.35rem' }}>
                  ลูกค้าที่เลือกภาษาอื่น (อังกฤษ/จีน/เขมร) จะเห็นคำแปลของระบบ ("Unlimited Drink Refill") แทนโดยอัตโนมัติ
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>
                  ราคา
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="number"
                    className="dark-input"
                    min="0"
                    step="1"
                    value={prices.drinkRefill}
                    onChange={(e) => handleChange('drinkRefill', e.target.value)}
                    style={{
                      width: '120px',
                      padding: '0.6rem 1rem',
                      fontSize: '1.1rem',
                      fontWeight: 700,
                      textAlign: 'right',
                      border: '1px solid var(--border-strong)',
                      borderRadius: '8px'
                    }}
                  />
                  <span style={{ fontWeight: 600, color: 'var(--muted)' }}>฿</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* เกณฑ์ส่วนสูง — ทุกหน้าในระบบอ่านป้ายจากตรงนี้ที่เดียว */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ marginBottom: '0.25rem' }}>📏 เกณฑ์ส่วนสูง</h3>
        <p style={{ fontSize: '0.85rem', color: 'var(--muted)', marginTop: 0, marginBottom: '1rem' }}>
          ตั้งที่นี่ที่เดียว แล้วทุกหน้าจะแสดงเหมือนกันหมด — หน้าจัดการโต๊ะ, หน้าเช็คบิล, ใบเสร็จ
          และหน้าสั่งอาหารของลูกค้า
        </p>

        <div style={{ display: 'grid', gap: '0.75rem' }}>
          {[
            { key: 'adultMin', label: '🧑 ผู้ใหญ่', hint: 'สูงตั้งแต่นี้ขึ้นไปคิดราคาผู้ใหญ่' },
            { key: 'childBigMin', label: '👦 เด็กโต', hint: 'สูงตั้งแต่นี้ขึ้นไปคิดราคาเด็กโต' },
            { key: 'childSmallMin', label: '🧒 เด็กเล็ก', hint: 'สูงตั้งแต่นี้ขึ้นไปคิดราคาเด็กเล็ก — ต่ำกว่านี้ฟรี' },
          ].map(({ key, label, hint }) => (
            <div key={key} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: '1rem', padding: '0.85rem 1rem', background: 'var(--surface-2)', borderRadius: '8px',
              color: 'var(--text)'
            }}>
              <div>
                <div style={{ fontWeight: 700 }}>{label}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>{hint}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="number"
                  className="dark-input"
                  min="1"
                  max="250"
                  step="1"
                  value={prices.heights?.[key] ?? ''}
                  onChange={(e) => handleHeightChange(key, e.target.value)}
                  style={{
                    width: '110px', padding: '0.6rem 1rem', fontSize: '1.1rem', fontWeight: 700,
                    textAlign: 'right', borderRadius: '8px',
                    border: heightError ? '1px solid var(--danger)' : '1px solid var(--border-strong)'
                  }}
                />
                <span style={{ fontWeight: 600, color: 'var(--muted)' }}>cm</span>
              </div>
            </div>
          ))}
        </div>

        {heightError ? (
          <div style={{
            marginTop: '1rem', padding: '0.75rem 1rem', background: '#2a1517',
            border: '1px solid #8a2f33', borderRadius: '8px', color: 'var(--danger)', fontWeight: 600
          }}>
            ⚠️ {heightError}
          </div>
        ) : (
          <div style={{
            marginTop: '1rem', padding: '0.75rem 1rem', background: 'var(--surface-2)',
            borderRadius: '8px', fontSize: '0.9rem', lineHeight: 1.9,
            color: 'var(--text)'
          }}>
            <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>สรุปเกณฑ์ที่จะใช้</div>
            <div>ผู้ใหญ่ — {labels.adult} · <strong>{prices.adult}฿</strong></div>
            <div>เด็กโต — {labels.childBig} · <strong>{prices.childBig}฿</strong></div>
            <div>เด็กเล็ก — {labels.childSmall} · <strong>{prices.childSmall}฿</strong></div>
            <div>เด็กเล็กมาก — {labels.childFree} · <strong>ฟรี</strong></div>
          </div>
        )}
      </div>

      {/* Preview */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ marginBottom: '1rem' }}>📋 ตัวอย่างการคำนวณ</h3>
        <div style={{ fontSize: '0.95rem', lineHeight: 1.8 }}>
          <div>ผู้ใหญ่ {exampleGuests.adults} คน × {prices.adult}฿ = <strong>{(exampleGuests.adults * prices.adult).toLocaleString()}฿</strong></div>
          <div>เด็กโต {exampleGuests.childrenBig} คน × {prices.childBig}฿ = <strong>{(exampleGuests.childrenBig * prices.childBig).toLocaleString()}฿</strong></div>
          <div>เด็กเล็ก {exampleGuests.childrenSmall} คน × {prices.childSmall}฿ = <strong>{(exampleGuests.childrenSmall * prices.childSmall).toLocaleString()}฿</strong></div>
          <div style={{ color: 'var(--muted)' }}>เด็ก {labels.childFree} — <strong>ฟรี ไม่คิดเงิน</strong></div>
          <hr style={{ margin: '0.5rem 0', border: 'none', borderTop: '2px solid var(--border)' }} />
          <div style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--accent)' }}>
            รวม: {exampleTotal.toLocaleString()}฿
          </div>
        </div>
      </div>

      <button
        onClick={handleSave}
        disabled={saving || !!heightError}
        className="button"
        style={{
          width: '100%',
          padding: '1rem',
          fontSize: '1.1rem',
          opacity: (saving || heightError) ? 0.7 : 1
        }}
      >
        {saving ? '⏳ กำลังบันทึก...' : heightError ? '⚠️ แก้ส่วนสูงให้ถูกก่อน' : '💾 บันทึกราคา'}
      </button>
    </div>
  );
}
